/* eslint-disable */
// ============================================================
//  LandingPage v2 — entrée principale + SplashScreenV2
//  Routeur : choisit la version selon ?v=1/2/3 et lazy-loade
//  uniquement la version active (chaque version = chunk webpack
//  séparé, économise ~50 KB sur le bundle initial).
//
//  Helpers partagés    → src/landing-shared.jsx
//  V1 cinématique      → src/landing-v1-cinematic.jsx
//  V2 photo immersive  → src/landing-v2-immersive.jsx
//  V3 démonstration    → src/landing-v3-showcase.jsx
//  PhoneMockup         → src/landing-phone-mockup.jsx
// ============================================================

import { lazy, Suspense, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { PoppingStars } from "./landing-shared";

const LandingV1Cinematic = lazy(() => import("./landing-v1-cinematic").then(m => ({ default: m.LandingV1Cinematic })));
const LandingV2Immersive = lazy(() => import("./landing-v2-immersive").then(m => ({ default: m.LandingV2Immersive })));
const LandingV3Showcase  = lazy(() => import("./landing-v3-showcase").then(m => ({ default: m.LandingV3Showcase  })));

// Fallback minimaliste pendant le chargement du chunk : juste un fond sombre
// cohérent avec le SplashScreen, évite un flash blanc.
function LandingFallback() {
  return (
    <div style={{
      minHeight: "100vh",
      background: "radial-gradient(ellipse at center, #2D1B4E 0%, #1A1A2E 50%, #0D0D1A 100%)",
    }} />
  );
}

// ============================================================
// SplashScreen v2 — version 2026
// Ambiance dark cohérente avec v1, logo en fade-in + halo lumineux pulsant,
// transition fluide. ~2,2 s.
// ============================================================
export function SplashScreenV2({ onDone }) {
  const [exiting, setExiting] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDone(true), 1700); // splash ~2 s (+ 0.3s de fondu)
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (!done) return;
    setExiting(true);
    const t = setTimeout(() => onDone && onDone(), 300);
    return () => clearTimeout(t);
  }, [done, onDone]);

  return (
    <motion.div
      onClick={() => setDone(true)}
      initial={{ opacity: 1 }}
      animate={{ opacity: exiting ? 0 : 1 }}
      transition={{ duration: 0.5 }}
      style={{
        minHeight: "100vh",
        background: "radial-gradient(ellipse at center, #2D1B4E 0%, #1A1A2E 50%, #0D0D1A 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
      }}
    >
      {/* Pluie d'étoiles qui pop sur toute la surface (en fond) */}
      <PoppingStars />

      {/* Halo lumineux pulsant derrière le logo */}
      <motion.div
        animate={{ opacity: [0.3, 0.6, 0.3], scale: [0.9, 1.1, 0.9] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        style={{
          position: "absolute",
          width: 360,
          height: 360,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(200,0,26,.4) 0%, transparent 65%)",
          filter: "blur(40px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* Logo central */}
      <motion.img
        src="/horseconnex_logo_sticker_rouge.png"
        alt=""
        initial={{ opacity: 0, scale: 0.85, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        style={{
          width: 200,
          height: 200,
          objectFit: "contain",
          filter: "drop-shadow(0 12px 40px rgba(200,0,26,.55))",
          position: "relative",
          zIndex: 1,
        }}
      />

      {/* Barre de chargement organique — discrète sous le logo */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        style={{
          marginTop: 36,
          width: 120,
          height: 2,
          background: "rgba(255,255,255,.1)",
          borderRadius: 2,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <motion.div
          initial={{ x: "-100%" }}
          animate={{ x: "100%" }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(90deg, transparent 0%, #C8001A 50%, transparent 100%)",
          }}
        />
      </motion.div>
    </motion.div>
  );
}

// ============================================================
// Sélecteur d'aperçu — affiché en bas quand on décommente plus bas.
// Garde en mémoire pour les démos / comparaisons internes.
// ============================================================
// eslint-disable-next-line no-unused-vars
function VersionSwitcher({ current }) {
  const switchTo = (v) => {
    const url = new URL(window.location.href);
    url.searchParams.set("v", String(v));
    window.location.href = url.toString();
  };
  return (
    <div style={{
      position: "fixed",
      bottom: 16,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 1000,
      background: "rgba(13,13,26,.92)",
      backdropFilter: "blur(10px)",
      borderRadius: 100,
      padding: "6px 8px",
      display: "flex",
      gap: 4,
      boxShadow: "0 8px 30px rgba(0,0,0,.3)",
      border: "1px solid rgba(255,255,255,.1)",
    }}>
      {[
        { v: 1, label: "Cinéma" },
        { v: 2, label: "Photo" },
        { v: 3, label: "Démo" },
        { v: 0, label: "v1 actuelle" },
      ].map(opt => (
        <button
          key={opt.v}
          onClick={() => switchTo(opt.v)}
          style={{
            background: current === opt.v ? "var(--red)" : "transparent",
            color: "#fff",
            border: "none",
            borderRadius: 100,
            padding: "8px 14px",
            fontSize: 11,
            fontWeight: 700,
            fontFamily: "var(--fh)",
            letterSpacing: 0.5,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ============================================================
// Export principal : choisit la version selon ?v=
// ============================================================
export function LandingPageV2({ onEnter, showToast }) {
  const urlVersion = (() => {
    try {
      const v = new URLSearchParams(window.location.search).get("v");
      const n = parseInt(v, 10);
      if (n >= 0 && n <= 3) return n;
    } catch (e) {}
    return 1; // default : v1 cinématique
  })();

  return (
    <Suspense fallback={<LandingFallback />}>
      {urlVersion === 1 && <LandingV1Cinematic onEnter={onEnter} />}
      {urlVersion === 2 && <LandingV2Immersive onEnter={onEnter} />}
      {urlVersion === 3 && <LandingV3Showcase onEnter={onEnter} />}
      {/* VersionSwitcher retiré de la prod (visible en dev en décommentant ci-dessous) */}
      {/* <VersionSwitcher current={urlVersion} /> */}
    </Suspense>
  );
}
